import { Affix, Drawer, Layout } from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidenav from "../Sidenav/Sidenav";
import Footer from "./Footer";
import Header from "./Header";
import Chatbot from "../chatbot/Chatbot";
import styles from "./Main.module.css";
import { checkTokenExp, logout } from "../../utils/checkTokenExp";
import getSetting from "../../api/getSettings";

const { Header: AntHeader, Content, Sider } = Layout;

const LIST_PAGE_PATHS = [
  "/product-category",
  "/supplier",
  "/book-publisher",
  "/product-currency",
  "/discount-master",
  "/purchaselist",
  "/purchase-order",
  "/customer",
  "/salelist",
  "/sales-order",
  "/transaction",
  "/account",
  "/location",
  "/hr/staffs",
  "/role",
  "/designation",
  "/product",
];

function Main({ children }) {
  // Default behavior:
  // - Desktop/web: sidebar expanded by default
  // - Mobile: sidebar collapsed by default
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 992;
  });

  const handleCollapsed = (val) => {
    setCollapsed(val);
  };

  // Keep default sidebar state aligned with current viewport
  useEffect(() => {
    const onResize = () => {
      setCollapsed(window.innerWidth < 992);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState("right");
  const [sidenavColor, setSidenavColor] = useState("#1890ff");
  const [sidenavType, setSidenavType] = useState("transparent");
  const [fixed, setFixed] = useState(false);

  const openDrawer = () => setVisible(!visible);
  const handleSidenavType = (type) => setSidenavType(type);
  const handleSidenavColor = (color) => setSidenavColor(color);
  const handleFixedNavbar = (type) => setFixed(type);

  const { pathname: originalPathname } = useLocation();
  const navigate = useNavigate();
  let pathname = originalPathname.replace("/", " ");

  // Check token validity on page load (only if user is logged in)
  useEffect(() => {
    const isLogged = localStorage.getItem("isLogged");
    if (isLogged) {
      const token = localStorage.getItem("access-token");
      if (token && !checkTokenExp(token)) {
        console.log("Token expired on page load, logging out...");
        logout();
      }
    }
  }, []);

  // Check if password change is required and redirect
  useEffect(() => {
    const isLogged = localStorage.getItem("isLogged");
    const isRequired = localStorage.getItem("isRequired");
    const isAuthPage = originalPathname.startsWith("/auth/");
    
    // Only redirect if user is logged in, password change is required, and not already on auth pages
    if (isLogged === "true" && isRequired === "true" && !isAuthPage) {
      navigate("/auth/change-password", { replace: true });
    }
  }, [originalPathname, navigate]);

  // Redirect to Invoice Setting if company has no invoice settings data
  useEffect(() => {
    const isLogged = localStorage.getItem("isLogged");
    const isAuthPage = originalPathname.startsWith("/auth/");
    const isInvoiceSettingPage = originalPathname === "/invoice-setting";
    if (!isLogged || isAuthPage || isInvoiceSettingPage) return;

    getSetting()
      .then((data) => {
        const setting = data?.result;
        const hasNoData = !setting || !(setting.company_name || "").trim();
        if (hasNoData) {
          navigate("/invoice-setting", { replace: true });
        }
      })
      .catch(() => {});
  }, [originalPathname, navigate]);

  useEffect(() => {
    if (pathname === "rtl") {
      setPlacement("left");
    } else {
      setPlacement("right");
    }
  }, [pathname]);

  // Close mobile menu drawer when route changes
  useEffect(() => {
    setVisible(false);
  }, [originalPathname]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  /* Only apply no-scroll layout on exact list paths so detail pages (e.g. /customer/6) can scroll */
  const isListPage = LIST_PAGE_PATHS.some((p) => originalPathname === p);

  return (
    <Layout className={styles.mainLayout}>
      {isLogged && (
        <Drawer
          title={false}
          placement={placement === "right" ? "left" : "right"}
          closable={false}
          onClose={() => setVisible(false)}
          visible={visible}
          key={placement === "right" ? "left" : "right"}
          width={220}
        >
          <Layout>
            <Sider
              trigger={null}
              width={220}
              theme="light"
              className={styles.siderDrawer}
            >
              <Sidenav color={sidenavColor} collapsed={false} />
            </Sider>
          </Layout>
        </Drawer>
      )}
      {isLogged && (
        <Sider
          breakpoint="lg"
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={220}
          theme="light"
          className={styles.siderMain}
        >
          {isLogged && <Sidenav color={sidenavColor} collapsed={collapsed} />}
        </Sider>
      )}
      <Layout
        className={
          isLogged
            ? collapsed
              ? `${styles.mainLayoutUncollapse} ${styles.mainContentLayout}`
              : `${styles.mainLayoutCollapse} ${styles.mainContentLayout}`
            : `${styles.mainLayoutMarginLeftZero} ${styles.mainContentLayout}`
        }
      >
        {fixed ? (
          <Affix>
            <AntHeader>
              <Header
                onPress={openDrawer}
                name={pathname}
                subName={pathname}
                currentPath={originalPathname}
                handleSidenavColor={handleSidenavColor}
                handleSidenavType={handleSidenavType}
                handleFixedNavbar={handleFixedNavbar}
                collapsed={collapsed}
                handleCollapsed={handleCollapsed}
                isLogged={isLogged}
              />
            </AntHeader>
          </Affix>
        ) : (
          <AntHeader>
            <Header
              onPress={openDrawer}
              name={pathname}
              subName={pathname}
              currentPath={originalPathname}
              handleSidenavColor={handleSidenavColor}
              handleSidenavType={handleSidenavType}
              handleFixedNavbar={handleFixedNavbar}
              collapsed={collapsed}
              handleCollapsed={handleCollapsed}
            />
          </AntHeader>
        )}
        <Content
          className={
            isListPage
              ? `${styles.mainContent} ${styles.contentNoScroll}`
              : styles.mainContent
          }
        >
          <div
            className={
              isListPage
                ? `${styles.contentInner} ${styles.contentInnerNoScroll}`
                : styles.contentInner
            }
          >
            {children}
          </div>
        </Content>
        <Footer />
      </Layout>
      {isLogged && <Chatbot />}
    </Layout>
  );
}

export default Main;
